import { format } from 'date-fns';
import { styled } from 'goober';
import { useTranslation } from 'react-i18next';

import type { RequestProjectResponse } from '@/api/v4/projects.api';
import { Button } from '@/ui/button/button';
import { Flexbox } from '@/ui/flexbox/flexbox';
import { LightTooltip } from '@/features/search/global-search/search-suggestions/enriched-table/ui';
import { Stack } from '@/ui/line/line';
import { modalState } from '@/state/modal.state';
import { useSetAtom } from 'jotai';

interface RequestedFromCellProps {
  request: RequestProjectResponse;
}

export const RequestedFromCell = ({ request }: RequestedFromCellProps) => (
  <RequestedFromContainer>
    <LightTooltip
      backgroundColor="white"
      content={
        <TooltipContentWrapper>
          <Stack gap="3px">
            <ContentText>{request.name}</ContentText>
          </Stack>
        </TooltipContentWrapper>
      }
      padding="14px 20px"
    >
      <RequesterName>{request.name}</RequesterName>
    </LightTooltip>
    <LightTooltip
      backgroundColor="white"
      content={
        <TooltipContentWrapper>
          <Stack gap="3px">
            <ContentText>{request.email}</ContentText>
          </Stack>
        </TooltipContentWrapper>
      }
      padding="14px 20px"
    >
      <RequesterEmail>{request.email}</RequesterEmail>
    </LightTooltip>
  </RequestedFromContainer>
);

interface DescriptionCellProps {
  description: string;
}

export const DescriptionCell = ({ description }: DescriptionCellProps) => (
  <DescriptionContainer>
    <LightTooltip
      backgroundColor="white"
      content={
        <TooltipContentWrapper>
          <Stack gap="3px">
            <ContentText>{description}</ContentText>
          </Stack>
        </TooltipContentWrapper>
      }
      padding="14px 20px"
    >
      <ContentText>{description}</ContentText>
    </LightTooltip>
  </DescriptionContainer>
);

interface DateCellProps {
  date: Date;
}

export const DateCell = ({ date }: DateCellProps) => {
  const formattedDate = format(date, 'dd MMM yyyy');
  return (
    <LightTooltip
      backgroundColor="white"
      content={
        <TooltipContentWrapper>
          <Stack gap="3px">
            <ContentText>{formattedDate}</ContentText>
          </Stack>
        </TooltipContentWrapper>
      }
      padding="14px 20px"
    >
      <DateText>{formattedDate}</DateText>
    </LightTooltip>
  );
};

interface BusinessUnitCellProps {
  businessUnit: string;
}

export const BusinessUnitCell = ({ businessUnit }: BusinessUnitCellProps) => (
  <LightTooltip
    backgroundColor="white"
    content={
      <TooltipContentWrapper>
        <Stack gap="3px">
          <ContentText>{businessUnit}</ContentText>
        </Stack>
      </TooltipContentWrapper>
    }
    padding="14px 20px"
  >
    <BusinessUnitText>{businessUnit}</BusinessUnitText>
  </LightTooltip>
);

interface ActionsCellProps {
  requestId: string;
}

export const ActionsCell = ({ requestId }: ActionsCellProps) => {
  const { t } = useTranslation('projects');
  const setModal = useSetAtom(modalState);

  const handleApprove = () => {
    setModal({
      state: 'approveProjectRequest',
      requestId,
    });
  };

  const handleReject = () => {
    setModal({
      state: 'rejectProjectRequest',
      requestId,
    });
  };

  return (
    <ActionButtonsContainer>
      <Button variant="primary" onClick={handleApprove}>
        {t`projects.pendingRequests.approve`}
      </Button>
      <Button variant="secondary" onClick={handleReject}>
        {t`projects.pendingRequests.reject`}
      </Button>
    </ActionButtonsContainer>
  );
};

const DateText = styled('div')`
  font-size: 14px;
  line-height: 145%;
  letter-spacing: 0.03em;
  color: ${({ theme }) => theme.colors.basics.black};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 120px;
`;

const BusinessUnitText = styled('div')`
  font-size: 14px;
  line-height: 145%;
  letter-spacing: 0.03em;
  color: ${({ theme }) => theme.colors.basics.black};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;
`;

const RequestedFromContainer = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const RequesterName = styled('div')`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.basics.black};
  font-size: 14px;
  line-height: 145%;
  letter-spacing: 0.03em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
`;

const RequesterEmail = styled('div')`
  font-weight: 400;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.gray.c10};
  line-height: 145%;
  letter-spacing: 0.03em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
`;

const DescriptionContainer = styled('div')`
  max-width: 100%;
  font-size: 14px;
  line-height: 145%;
  letter-spacing: 0.03em;
  color: ${({ theme }) => theme.colors.basics.black};
`;

const ActionButtonsContainer = styled(Flexbox)`
  gap: 8px;
  align-items: center;
`;

const TooltipContentWrapper = styled('div')`
  max-width: 400px;
  max-height: 300px;
  overflow-y: auto;
  text-align: left;
  color: ${({ theme }) => theme.colors.gray.c12};
`;

const ContentText = styled('div')`
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
  word-wrap: break-word;
  line-height: 1.4;
  font-size: 12px;
`;
